import React, { useState } from 'react';
import { Modal, Input, Alert, Select, Form, message } from 'antd';
import useProductGroupOpenApi, { ProductGroupCreateRequest, ProductGroupType } from 'apiClients/useProductGroupOpenApiClient';
import { getProductGroupTypeOptions } from 'utility/helper';

const { Option } = Select;

interface ProductGroupCreateDialogProps {
  onClose: () => void;
  isOpen: boolean;
  onSuccess?: () => void;
}

const ProductGroupCreateDialog: React.FC<ProductGroupCreateDialogProps> = ({
  onClose,
  isOpen,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { createProductGroup, loading } = useProductGroupOpenApi();

  const productGroupTypes = getProductGroupTypeOptions();

  const handleSubmit = async (values: any) => {
    try {
      setIsError(false);
      setErrorMessage('');

      const createRequest: ProductGroupCreateRequest = {
        name: values.name?.trim(),
        type: values.type,
        languageId: values.languageId || 1, // Default to Swedish (1)
      };

      const result = await createProductGroup(createRequest);
      
      if (result) {
        message.success('Produktgrupp skapad framgångsrikt');
        form.resetFields();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setIsError(true);
        setErrorMessage('Fel vid skapande av produktgrupp');
      }
    } catch (error) {
      setIsError(true);
      setErrorMessage('Ett oväntat fel inträffade');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsError(false);
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal
      title="Skapa ny produktgrupp"
      open={isOpen}
      onCancel={handleCancel}
      okText="Skapa"
      cancelText="Avbryt"
      confirmLoading={loading}
      onOk={() => form.submit()}
    >
      <div style={{ marginBottom: "1rem" }}>
        Fyll i informationen för att skapa en ny produktgrupp.
      </div>
      
      {isError && (
        <Alert
          style={{ marginBottom: "1rem" }}
          message={errorMessage || "Ett fel inträffade vid skapande av produktgrupp"}
          type="error"
          showIcon
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: 0, // Default to Category
          languageId: 1, // Default to Swedish
        }}
      >
        <Form.Item
          label="Namn"
          name="name"
          rules={[
            { required: true, message: 'Namn är obligatoriskt' },
            { min: 2, message: 'Namn måste vara minst 2 tecken' },
            { max: 100, message: 'Namn får inte vara längre än 100 tecken' },
          ]}
        >
          <Input
            placeholder="Ange produktgruppens namn"
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          label="Typ"
          name="type"
          rules={[{ required: true, message: 'Typ är obligatorisk' }]}
        >
          <Select placeholder="Välj produktgruppstyp">
            {productGroupTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Språk"
          name="languageId"
          rules={[{ required: true, message: 'Språk är obligatoriskt' }]}
        >
          <Select placeholder="Välj språk">
            <Option value={1}>Svenska</Option>
            <Option value={2}>Norska</Option>
            <Option value={3}>Danska</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductGroupCreateDialog;
